import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../../components/BaseUI/Modal";
import Button from "../../../../components/BaseUI/Button";
import Input from "../../../../components/BaseUI/Input";
import Label from "../../../../components/BaseUI/Label";
import Select from "../../../../components/BaseUI/Select";
import { dateStrInAppTz } from "../../../../utils/appTime";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const todayStr = () => dateStrInAppTz(new Date());

const ProductionEntryModal = ({
  isOpen,
  onClose,
  machine,
  initialPartId,
  getToken,
  onSuccess,
}) => {
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [partId, setPartId] = useState("");
  const [qty, setQty] = useState("");
  const [date, setDate] = useState(todayStr());

  const headers = useCallback(
    () => ({ Authorization: `Bearer ${getToken()}` }),
    [getToken],
  );

  const resetForm = useCallback(() => {
    setFormData(null);
    setError("");
    setPartId(initialPartId ? String(initialPartId) : "");
    setQty("");
    setDate(todayStr());
  }, [initialPartId]);

  useEffect(() => {
    if (!isOpen) return;
    resetForm();
  }, [isOpen, resetForm]);

  const fetchFormData = useCallback(async () => {
    if (!machine?._id) return;
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({
        machineId: machine._id,
        action: "production",
      });
      const { data } = await axios.get(
        `${API_URL}/api/epc/form-data?${params}`,
        { headers: headers() },
      );
      setFormData(data);
    } catch (err) {
      setError(
        err?.response?.data?.message || "Form verileri yüklenemedi",
      );
    } finally {
      setLoading(false);
    }
  }, [machine?._id, headers]);

  useEffect(() => {
    if (isOpen && machine?._id) {
      fetchFormData();
    }
  }, [isOpen, machine?._id, fetchFormData]);

  useEffect(() => {
    if (!isOpen || !initialPartId || partId) return;
    setPartId(String(initialPartId));
  }, [isOpen, initialPartId, partId]);

  const partOpts = useMemo(
    () =>
      [
        ...new Map(
          (formData?.productionOptions || []).map((o) => [
            o.partId,
            {
              value: o.partId,
              label: o.partName,
            },
          ]),
        ).values(),
      ],
    [formData?.productionOptions],
  );

  const qtyNum = Math.max(0, Number(qty) || 0);
  const canSave = !!partId && qtyNum > 0 && !!date && partOpts.length > 0;

  const handleSave = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    setError("");
    try {
      await axios.post(
        `${API_URL}/api/epc/production`,
        {
          partId,
          machineId: machine._id,
          producedNow: qtyNum,
          date,
        },
        { headers: headers() },
      );
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || "Üretim kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const machineLabel = machine?.name || machine?.code || "";

  return (
    <Modal
      isOpen={isOpen}
      width="medium"
      scrollBehavior="inside"
      onClose={saving ? undefined : onClose}
      className="mlm-entry-modal"
    >
      <ModalHeader>
        Üretim Girişi{machineLabel ? ` — ${machineLabel}` : ""}
      </ModalHeader>
      <ModalBody showCloseButton={false}>
        {loading ? (
          <div className="mlm-entry-modal__loading">Form yükleniyor...</div>
        ) : (
          <div className="mlm-entry-form">
            <div className="mlm-entry-form__field">
              <Select
                label="Üretilen Parça / Ürün"
                required
                unique
                disabled={saving || partOpts.length === 0}
                placeholder={
                  partOpts.length === 0
                    ? "Bu makine için tanımlı üretim hattı yok"
                    : "Parça seçiniz"
                }
                options={partOpts}
                selected={partId ? [partId] : []}
                onSelected={(items) =>
                  setPartId(items?.[0]?.value ? String(items[0].value) : "")
                }
              />
            </div>

            <div className="mlm-entry-form__row">
              <div className="mlm-entry-form__field">
                <Label required>Üretim Miktarı (adet)</Label>
                <Input
                  type="number"
                  min="1"
                  value={qty}
                  onChange={setQty}
                  placeholder="0"
                  disabled={saving}
                />
              </div>
              <div className="mlm-entry-form__field">
                <Label required>Tarih</Label>
                <Input
                  type="date"
                  value={date}
                  onChange={setDate}
                  disabled={saving}
                />
              </div>
            </div>
          </div>
        )}
        {error ? <div className="mlm-entry-form__error">{error}</div> : null}
      </ModalBody>
      <ModalFooter>
        <Button type="button" disabled={saving} onClick={onClose}>
          İptal
        </Button>
        <Button
          appearance="primary-animated"
          type="button"
          disabled={saving || loading || !canSave}
          onClick={handleSave}
        >
          {saving ? "Kaydediliyor..." : "Üretimi Kaydet"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ProductionEntryModal;
